import React, { useEffect, useState } from 'react';
import PreferencesSection from '../PreferencesSection';
import useStarfishAPI from 'hooks/useStarfishAPI';
import { SFEntityID } from 'utils/types';
import css from './CalendarManagers.scss';
import SortableTable from 'SF/table/SortableTable';
import CalendarManagerRow from './CalendarManagerRow';
import PropTypes from 'prop-types';
import CalendarLockIcon from 'common/CalendarLockIcon';
import Icon from 'SF/MaterialIcon';
import { urlParams } from 'utils/request';
import { getWord } from "utils/tenant";
import ConfirmDialog from 'common/ConfirmDialog';
import { some } from 'lodash';
import AddCalendarManagerModal from './AddCalendarManagerModal';
import Toast from 'common/Toast';

const CalendarManagers = ({ calendarLockingEnabled, isCalendarLocked, isIE, userId }) => {
    const [ managersResponse, callManagersAPI ] = useStarfishAPI();
    const [ deleteResponse, callDeleteAPI ] = useStarfishAPI();
    const [ managers, setManagers ] = useState([]);
    const [ showCalendarManagerInfoTooltip, setShowCalendarManagerInfoTooltip ] = useState(false);
    const [ showCalendarAdminInformation, setShowCalendarAdminInformation ] = useState(false);
    const [ isModalOpen, setModalOpen ] = useState(false);
    const [ showToast, setShowToast ] = useState(false);
    const [ toastMessage, setToastMessage] = useState(null);

    const loadCalendarManagers = () => callManagersAPI('../instructor/getCalendarManagersList.html');
    
    useEffect(() => {
        loadCalendarManagers();
    }, [userId]);
    
    useEffect(() => {
        if (managersResponse.success)
            setManagers(managersResponse.data.result);
    }, [managersResponse]);

    // calendar admin info should show when calendar locking is enabled AND the user
    // either has a locked calendar or at least one calendar admin
    useEffect(() => {
        setShowCalendarAdminInformation(
            calendarLockingEnabled &&
            (isCalendarLocked || some(managers, ['calendarAdministrator', true])));
    }, [managers, isCalendarLocked, calendarLockingEnabled]);
    
    const deleteCalendarManager = async (calendarManagerId) =>
        await callDeleteAPI('../instructor/deleteCalendarManager.html', {
            method: 'POST',
            body: urlParams({ calMgrId: calendarManagerId })
        });
    
    useEffect(() => {
        if (deleteResponse.success) {
            loadCalendarManagers();
            setToastMessage('Calendar manager deleted successfully.');
            setShowToast(true);
        }
    }, [deleteResponse]);
    
    const getCalendarAdminInformation = () => {
        if (!showCalendarAdminInformation) return null;
        return (
            <div className={css['calendar-lock-info']}>
                    <span className={css['lock-info']}>
                        <CalendarLockIcon />Indicates a Calendar Administrator. Contact them to make changes to your locked calendar.
                    </span>
                <p>
                    If your calendar is <span className={css['bold']}>locked</span> <Icon className={css['lock']} icon="lock" />, your Calendar
                    Administrator(s) can add and edit your {getWord('officeHours', true)}, group sessions, and reserve time. You and
                    those listed below can view your calendar as well as schedule and edit appointments within
                    already existing schedule blocks.
                </p>
                <p>
                    If your calendar is <span className={css['bold']}>unlocked</span>, you and those listed below have all previously mentioned privileges.
                </p>
            </div>
        );
    };
    
    const getHeaders = () => {
        const headers = [];
        if (showCalendarAdminInformation) headers.push({ content: '' });
        headers.push({ content: 'Name' }, { content: '' });
        return headers;
    };
    
    const renderManager = (manager, index) => 
        <CalendarManagerRow {...manager}
                            calendarManagerInfo={() => setShowCalendarManagerInfoTooltip(true)}
                            deleteCalendarManager={deleteCalendarManager}
                            key={index}
                            showCalendarAdminInformation={showCalendarAdminInformation} />;
    
    const getManagersTable = () => {
        if (managers.length === 0) return <div className={css['no-managers']}>No calendar managers added</div>;
        return (
            <SortableTable columnToSort="name"
                           headers={getHeaders()}
                           items={managers}
                           shouldPaginate={false}
                           renderItem={renderManager} />
        );
    };
    
    return (
        <PreferencesSection isStaff
                            headerText="Calendar Management"
                            descriptionText="Customize and view people who manage your calendar."
                            className={css['manager-table']}>
            {getCalendarAdminInformation()}
            <button className={css['add-btn']}
                    onClick={() => setModalOpen(true)}>
                <Icon icon="add_circle" aria-hidden={true} />Add Calendar Manager
            </button>
            {getManagersTable()}
            <ConfirmDialog headerText="Notice"
                           descriptionText="You cannot remove this Calendar Manager because they were added by your System Administrator."
                           onConfirm={() => setShowCalendarManagerInfoTooltip(false)}
                           confirmText="OK"
                           isOpen={showCalendarManagerInfoTooltip} />
            <AddCalendarManagerModal closeModal={() => setModalOpen(false)}
                                     isIE={isIE}
                                     isOpen={isModalOpen}
                                     onSave={() => {
                                         setToastMessage("Calendar manager added successfully.");
                                         setShowToast(true);
                                         loadCalendarManagers();
                                         setModalOpen(false);
                                     }} />
            {showToast && <Toast message={toastMessage} onTimeout={() => setShowToast(false)} />}
        </PreferencesSection>
    );
};

CalendarManagers.propTypes = {
    calendarLockingEnabled: PropTypes.bool,
    isCalendarLocked: PropTypes.bool,
    isIE: PropTypes.bool,
    userId: SFEntityID.isRequired
};

export default CalendarManagers;
